import classNames from "classnames";
import Async from "components/DesignSystem/AsyncComponents/Async";
import { Pagination } from "components/DesignSystem/Pagination/Pagination";
import { Search } from "components/DesignSystem/Search/Search";
import Table from "components/DesignSystem/Table/V2/Table";
import { CustomTaxOthersAccordion } from "components/TaxOthers/CustomAccordionComponents";
import { ConditionalLink } from "components/conditionalLink";
import DashboardContainer from "components/dashboard/DashboardContainer";
import { CaretDown } from "components/icons/CaretDown";
import TaskTabBar from "components/tasks/taskTabBar";
import { NO_TAG, NO_TAG_KEY } from "constants/tagColors";
import { ALL, ALL_BANK_CONNECTED, ANY_BANK_DISCONNECTED } from "constants/task";
import { useQuery, useUpdateQuery } from "hooks/useQuery";
import authContext from "jwt_context&axios/authContext";
import { EmptyScreen } from "pages/Books/EmptyScreen";
import { useContext, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "react-table-8.10.7";
import { useGetAllBooksEntityQuery } from "store/apis/bookkeepingTracker";
import { useGetTaskTagQuery } from "store/apis/taskTags";
import { EntityWithTask } from "types/Models/bookkeepingTracker";
import { debounce } from "utils/debouncing";
import { BookkeepingTrackerFilter } from "./BookkeepingTrackerFilter";
import { BookkeepingTrackerSlider } from "./BookkeepingTrackerSlider";
import { BookkeepingTrackerSoftware } from "./BookkeepingTrackerSoftware";
import { BookkeepingTrackerTags } from "./BookkeepingTrackerTags";
import { EntityTaskTable } from "./EntityTaskTable";
import { GroupNote } from "./GroupNote";
import { StatusTab } from "./StatusTab";
import { usePagination } from "hooks/usePagination";

const FilterTab = ({ searchTerm }: { searchTerm: string | null }) => {
  const { update } = useUpdateQuery();

  const handleSearch = debounce((e) => {
    const { value }: { value: string } = e.target;
    update({ query: "search_term", value });
  });
  return (
    <div>
      <Search
        customSize="regular"
        placeholder="Search.."
        block
        onChange={handleSearch}
        className="t-h-11 t-rounded-lg"
        defaultValue={searchTerm || ""}
      />
    </div>
  );
};

export const BookkeepingTracker = () => {
  const query = useQuery();
  const pageNumber = Number(query.get("page") || 1);
  const searchTerm = query.get("search_term") || null;
  const selectedTag = query.get("task_tag_filter_uuids") || null;
  const selectedCompanyTag = query.get("company_tag_filter_uuids") || null;
  const selectedBankConnection = query.get("bank_connections") || null;
  const selectedServicesFilter = query.get("service_task_filter_uuids") || null;
  const selectedSeason = query.get("season_filter_uuids") || null;
  const selectedTeams = query.get("service_team_uuids") || null;
  const selectedMembers = query.get("team_member_uuids") || null;
  const status = query.get("status") || "ALL";
  const showUnassignedTeam = query.get("show_unassigned_team") || null;
  const showUnassignedBookkeeper =
    query.get("show_unassigned_bookkeeper") || null;
  const selectedFormationType =
    query.get("formation_type_filter_uuids") || null;
  const selectedSort = query.get("task_sort_by") || null;
  const selectedAccountantAccess = query.get("accountant_access") || null;
  const { practicecrmId } = useParams<{ practicecrmId?: string }>();
  const { authtoken } = useContext(authContext);
  const [totalPage, setTotalPage] = useState(1);
  const serviceTeamId = practicecrmId || authtoken.service_team_id;

  const { update } = useUpdateQuery();
  const { data: tagsData = [] } = useGetTaskTagQuery();

  const onPageNumChange = (currentPage: number) => {
    update({ query: "page", value: currentPage });
  };

  const { pageNum, goToFirstPage, goToPrevPage, goToNextPage, goToLastPage } =
    usePagination({ pageNumber, onPageNumChange, totalPage });

  const filters = {
    search_term: searchTerm,
    page_num: pageNum,
    task_tag_filter_uuids: selectedTag,
    company_tag_filter_uuids: selectedCompanyTag,
    task_sort_by: selectedSort || null,
    task_list_type: status === ALL ? null : status,
    show_unassigned_preparer: Boolean(showUnassignedBookkeeper) || null,
    show_unassigned_team: Boolean(showUnassignedTeam) || null,
    service_team_ids: selectedTeams,
    preparer_ids: selectedMembers,
    formation_types: selectedFormationType,
    service: selectedServicesFilter,
    seasons: selectedSeason,
    is_all_bank_connected:
      selectedBankConnection === ALL_BANK_CONNECTED || null,
    is_any_bank_disconnected:
      selectedBankConnection === ANY_BANK_DISCONNECTED || null,
    accountant_access_status: selectedAccountantAccess || null,
  };

  const { data, isLoading, isSuccess } = useGetAllBooksEntityQuery(filters);

  const {
    entities = [],
    per_page = 1,
    total_count = 1,
    total_pages = 1,
  } = data || {};

  useEffect(() => {
    setTotalPage(total_pages);
  }, [total_pages]);

  useEffect(() => {
    update({ query: "page", value: 1 });
  }, [status]);

  const tags = useMemo(
    () => [
      ...tagsData,
      {
        title: NO_TAG,
        uuid: NO_TAG_KEY,
        bg_color_hex: "",
        color_hex: "",
        tag_category: 0,
      },
    ],
    [tagsData]
  );

  const paginationData = {
    totalPage: total_pages,
    currentPage: pageNumber,
    itemsPerPage: per_page,
    totalItemCount: total_count,
  };

  const isEmpty = entities.length === 0;

  const columnHelper = createColumnHelper<EntityWithTask>();
  const columns = useMemo(() => {
    return [
      columnHelper.accessor("name", {
        size: 20,
        header: "ENTITY AND GROUP",
        cell: (info) => {
          const rowData = info.row.original;

          return (
            <div className="t-flex t-gap-6 t-items-center">
              <span className="group-data-state-open:t-rotate-0 group-data-state-closed:-t-rotate-90 t-text-neutral t-transform t-transition t-duration-300 t-ease-in-out">
                <CaretDown />
              </span>
              <div>
                <ConditionalLink
                  to={`/admin/crm/${rowData.group.uuid}/entity/${rowData.uuid}`}
                  className="t-text-body t-text-purple"
                >
                  {info.getValue()}
                </ConditionalLink>
                <div className="t-text-body-sm t-text-text-30">
                  {rowData.group.name}{" "}
                </div>
              </div>
            </div>
          );
        },
      }),
      columnHelper.accessor("group.tags", {
        size: 20,
        header: "GROUP TAGS",
        cell: ({ row }) => {
          return <BookkeepingTrackerTags group={row.original.group} />;
        },
      }),
      columnHelper.accessor("number_of_transactions", {
        size: 15,
        header: () => <div className="t-text-right">TRANSACTIONS</div>,
        cell: (info) => (
          <div className="t-text-body t-text-text-60 t-text-right">
            {info.getValue()}
          </div>
        ),
      }),
      columnHelper.accessor("total_bank_connections", {
        size: 15,
        header: () => <div className="t-text-right">CONNECTIONS</div>,
        cell: (info) => {
          const { total_bank_connections, active_bank_connections } =
            info.row.original;

          return (
            <div
              className={classNames("t-text-right", {
                "t-text-red":
                  total_bank_connections !== active_bank_connections,
                "t-text-green-80":
                  total_bank_connections === active_bank_connections,
              })}
            >
              {active_bank_connections}/{total_bank_connections}
            </div>
          );
        },
      }),

      columnHelper.accessor("bookkeeping_software", {
        size: 20,
        header: "SOFTWARE",
        cell: (info) => {
          const rowData = info.row.original;

          return (
            <BookkeepingTrackerSoftware
              groupId={rowData.group.uuid}
              entityId={rowData.uuid}
              bookkeepingSoftware={info.getValue()}
            />
          );
        },
      }),
      columnHelper.accessor("group.uuid", {
        header: "",
        size: 5,
        cell: ({ row }) => {
          return (
            <div
              className="t-flex t-justify-center"
              onClick={(e) => e.stopPropagation()}
            >
              <GroupNote group={row.original.group} />
            </div>
          );
        },
      }),
    ];
  }, [status]);
  const tableData = useMemo(() => {
    return entities;
  }, [entities]);

  const table = useReactTable({
    data: tableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <DashboardContainer className="t-h-[calc(100%-80px)]">
      <TaskTabBar defaultValue="admin/bookkeeping-tracker" />
      <div className="t-m-5">
        <div className="t-flex t-justify-between t-items-center t-mb-3 ">
          <div className="t-flex t-gap-4 t-w-2/3">
            <StatusTab
              status={status}
              setStatus={(value) => update({ query: "status", value })}
            />
            <FilterTab searchTerm={searchTerm} />
          </div>
          <Pagination
            {...paginationData}
            goToFirstPage={goToFirstPage}
            goToPrevPage={goToPrevPage}
            goToNextPage={goToNextPage}
            goToLastPage={goToLastPage}
          />
        </div>
        <BookkeepingTrackerFilter
          selectedTag={selectedTag}
          selectedCompanyTag={selectedCompanyTag}
          tags={tags}
          selectedServicesFilter={selectedServicesFilter}
          taskStates={[]}
          selectedSeason={selectedSeason}
          selectedSort={selectedSort}
          showSortFeature={true}
          selectedTeams={selectedTeams}
          selectedMembers={selectedMembers}
          serviceTeamId={serviceTeamId}
          showUnassignedTeam={showUnassignedTeam}
          showUnassignedBookkeeper={showUnassignedBookkeeper}
          selectedBankConnection={selectedBankConnection}
          selectedFormationType={selectedFormationType}
          selectedAccountAccess={selectedAccountantAccess}
        />
      </div>
      <div className="t-m-5">
        <Async.Root {...{ isLoading, isSuccess, isEmpty }}>
          <Async.Empty>
            <div className="t-flex t-items-center t-h-80">
              <EmptyScreen text="No Bookkeeping found" className="!t-h-full" />
            </div>
          </Async.Empty>
          <Async.Success>
            <CustomTaxOthersAccordion.Root type="single" collapsible>
              <Table.Head
                className={classNames(
                  "t-inline-table t-w-full t-bg-neutral-10 !t-border-b",
                  {
                    "!t-rounded-t-none": false,
                  }
                )}
              >
                {table.getHeaderGroups().map((headerGroup) => (
                  <Table.HeadRow key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <Table.HeadCell
                        key={header.id}
                        style={{ width: `${header.getSize()}%` }}
                        className="t-py-4 t-px-3 t-uppercase t-text-subtext-sm"
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                      </Table.HeadCell>
                    ))}
                  </Table.HeadRow>
                ))}
              </Table.Head>
              {table.getRowModel().rows.map((row) => (
                <CustomTaxOthersAccordion.Item
                  key={row.id}
                  value={row.id}
                  propClass="!t-pb-0"
                >
                  <CustomTaxOthersAccordion.Trigger className="t-group data-[state=open]:t-border data-[state=open]:t-border-solid data-[state=open]:t-border-l-0 data-[state=open]:t-border-t-0 data-[state=open]:t-border-r-0 data-[state=open]:t-border-b-neutral-10">
                    <Table.Row className={classNames("t-cursor-pointer")}>
                      {row.getVisibleCells().map((cell) => (
                        <Table.Cell
                          key={cell.id}
                          style={{ width: `${cell.column.getSize()}%` }}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </Table.Cell>
                      ))}
                    </Table.Row>
                  </CustomTaxOthersAccordion.Trigger>
                  <CustomTaxOthersAccordion.Content className="t-bg-surface-lighter-grey t-w-full t-overflow-x-auto">
                    <EntityTaskTable
                      entityId={row.original.uuid}
                      group={row.original.group}
                      bookkeepingSoftware={row.original.bookkeeping_software}
                      connectionDetails={row.original.connection_details}
                      filters={filters}
                    />
                  </CustomTaxOthersAccordion.Content>
                </CustomTaxOthersAccordion.Item>
              ))}
            </CustomTaxOthersAccordion.Root>
          </Async.Success>
        </Async.Root>
      </div>
      <BookkeepingTrackerSlider status={status} />
    </DashboardContainer>
  );
};
